import { Box, Typography, Tooltip } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import FiberSmartRecordOutlinedIcon from '@mui/icons-material/FiberSmartRecordOutlined';
import { useLanguage } from '../../../../utils/i18n';

import tireOn from 'assets/images/icons/tireOn.svg';
import tireOff from 'assets/images/icons/tireOff.svg';

export const parseAxleConfig = (axleConfigData) => {
  if (!axleConfigData || !axleConfigData.info || !Array.isArray(axleConfigData.info)) return [];

  return axleConfigData.info.map((axleInfo) => {
    const left = [];
    const right = [];

    const sortedPositions = [...(axleInfo.positions || [])].sort((a, b) => String(a.order).localeCompare(String(b.order)));

    sortedPositions.forEach((p) => {
      const code = (p.code || '').toUpperCase();
      if (code.includes('L')) {
        left.push({ key: p.code, label: p.label || p.code });
      } else if (code.includes('R')) {
        right.push({ key: p.code, label: p.label || p.code });
      } else {
        if (left.length < sortedPositions.length / 2) {
          left.push({ key: p.code, label: p.label || p.code });
        } else {
          right.push({ key: p.code, label: p.label || p.code });
        }
      }
    });

    return {
      id: `axle_${axleInfo.axle_number}`,
      title: `Axle ${axleInfo.axle_number}`,
      is_steering: !!axleInfo.is_steering,
      is_driver: !!axleInfo.is_driver,
      is_lift_axle: !!axleInfo.is_lift_axle,
      is_twin_tire: !!axleInfo.is_twin_tire,
      left,
      right
    };
  });
};

export const InteractiveTire = ({
  posKey,
  posLabel,
  index,
  isInstalled,
  isSelected,
  isSelectedTire,
  isTarget,
  onClick,
  disabled,
  tooltipData
}) => {
  const { t } = useLanguage();
  const tooltipContent = tooltipData ? (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
        <strong>{t('tireCardHistory.serialNumber', 'Serial Number')}:</strong> {tooltipData.serial_number || '-'}
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
        <strong>{t('general.airPressure', 'Air Pressure')}:</strong>{' '}
        {tooltipData.lastInspection?.air_pressure !== null && tooltipData.lastInspection?.air_pressure !== undefined
          ? `${tooltipData.lastInspection.air_pressure} PSI`
          : '-'}
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
        <strong>{t('general.treadDepth', 'Tread Depth')}:</strong>{' '}
        {tooltipData.lastInspection?.tread_depth !== null && tooltipData.lastInspection?.tread_depth !== undefined
          ? `${tooltipData.lastInspection.tread_depth} mm`
          : '-'}
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
        <strong>{t('general.pattern', 'Pattern')}:</strong> {tooltipData.tire_spec_name || '-'}
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
        <strong>{t('general.brand', 'Brand')}:</strong> {tooltipData.tire_brand_name || '-'}
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
        <strong>{t('general.size', 'Size')}:</strong> {tooltipData.tire_size_code || '-'}
      </Typography>
    </Box>
  ) : null;

  return (
    <Tooltip
      title={tooltipContent}
      open={!!(isSelectedTire && tooltipData)}
      placement="top"
      arrow
      disableHoverListener
      disableFocusListener
      disableTouchListener
    >
      <Box
        onClick={(e) => {
          if (!disabled) {
            e.stopPropagation();
            onClick(posKey);
          }
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          transition: 'all 0.2s',
          transform: isSelected || isTarget || isSelectedTire ? 'scale(1.1)' : 'scale(1)',
          position: 'relative',
          '&:hover': { transform: !disabled && !isSelected && !isTarget && !isSelectedTire ? 'scale(1.05)' : undefined }
        }}
      >
        {(isSelected || isTarget || isSelectedTire) && (
          <Box
            sx={{
              position: 'absolute',
              top: 36,
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 80,
              height: 80,
              bgcolor: isTarget ? 'warning.main' : 'primary.main',
              opacity: 0.2,
              borderRadius: '50%',
              filter: 'blur(8px)',
              zIndex: 0
            }}
          />
        )}
        <Box sx={{ position: 'relative', width: 72, height: 72, zIndex: 1 }}>
          <Box
            component="img"
            src={isInstalled ? tireOn : tireOff}
            alt={posKey}
            sx={{ width: '100%', height: '100%', filter: isSelected ? 'drop-shadow(0px 4px 8px rgba(0,0,0,0.4))' : 'none' }}
          />
          <Typography
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#333',
              fontWeight: 900,
              fontSize: '1.2rem',
              userSelect: 'none',
              textShadow: '-1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff, 0 0 8px rgba(255,255,255,1)'
            }}
          >
            {index}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            fontWeight: isSelected || isSelectedTire ? 700 : 500,
            color: isSelected || isSelectedTire ? 'primary.main' : 'text.secondary',
            fontSize: '0.65rem'
          }}
        >
          {posLabel}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export const AxleMap = ({
  axles,
  positionMap,
  isPositionFilled,
  selectedPosition,
  selectedTire,
  selectedTireData,
  targetPosition,
  handlePositionClick,
  onEmptyAreaClick
}) => {
  const { t } = useLanguage();
  if (axles.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          {t('tireCycle.noAxleConfigurationAvailable', 'No Axle Configuration Available')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 520,
        py: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        alignItems: 'stretch',
        height: 'max-content'
      }}
      onClick={() => onEmptyAreaClick && onEmptyAreaClick()}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 60,
          bottom: 60,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 8,
          bgcolor: '#e0e0e0',
          zIndex: 0,
          borderRadius: 4
        }}
      />
      {axles.map((axle) => (
        <Box
          key={axle.id}
          sx={{
            position: 'relative',
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box sx={{ position: 'absolute', left: 40, right: 40, height: 8, bgcolor: '#e0e0e0', zIndex: 0, borderRadius: 4 }} />

          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 3,
              display: 'flex',
              gap: 0.5,
              bgcolor: '#ffffff',
              px: 1,
              py: 0.5,
              borderRadius: 4,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              border: '1px solid #e2e8f0',
              alignItems: 'center'
            }}
          >
            {axle.is_steering && (
              <>
                <Tooltip title={t('tireCycle.steeringAxle', 'Steering Axle')}>
                  <SwapHorizIcon sx={{ fontSize: 16, color: '#0ea5e9' }} />
                </Tooltip>
              </>
            )}
            {axle.is_twin_tire && (
              <>
                <Tooltip title={t('tireCycle.twinTireAxle', 'Twin Tire Axle')}>
                  <FiberSmartRecordOutlinedIcon sx={{ fontSize: 16, color: '#3995c0' }} />
                </Tooltip>
              </>
            )}
            {axle.is_driver && (
              <>
                <Tooltip title={t('tireCycle.drivenAxle', 'Driven Axle')}>
                  <SettingsIcon sx={{ fontSize: 16, color: '#334155' }} />
                </Tooltip>
              </>
            )}
            {axle.is_lift_axle && (
              <>
                <Tooltip title={t('tireCycle.liftAxle', 'Lift Axle')}>
                  <KeyboardDoubleArrowUpIcon sx={{ fontSize: 16, color: '#8b5cf6' }} />
                </Tooltip>
              </>
            )}
            {!axle.is_twin_tire && !axle.is_driver && !axle.is_lift_axle && (
              <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#64748b' }}>
                -
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 3, zIndex: 2 }}>
            {axle.left.map((pos) => (
              <InteractiveTire
                key={pos.key}
                posKey={pos.key}
                posLabel={pos.key}
                index={positionMap[pos.key]?.index}
                isInstalled={isPositionFilled(pos.key)}
                isSelected={selectedPosition === pos.key}
                isSelectedTire={selectedTire === pos.key}
                tooltipData={selectedTire === pos.key ? selectedTireData : null}
                isTarget={targetPosition === pos.key}
                onClick={handlePositionClick}
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 3, zIndex: 2 }}>
            {axle.right.map((pos) => (
              <InteractiveTire
                key={pos.key}
                posKey={pos.key}
                posLabel={pos.key}
                index={positionMap[pos.key]?.index}
                isInstalled={isPositionFilled(pos.key)}
                isSelected={selectedPosition === pos.key}
                isSelectedTire={selectedTire === pos.key}
                tooltipData={selectedTire === pos.key ? selectedTireData : null}
                isTarget={targetPosition === pos.key}
                onClick={handlePositionClick}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
